import * as Sentry from "@sentry/nextjs";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import React, { FC } from "react";

import { DialogEnum } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { HeartIconFill } from "@/features/common/components/icons/HeartIconFill";
import { useUIContext } from "@/features/common/hooks/UIContext/useUIContext";
import { useSnackbar } from "@/features/common/hooks/snackbar/useSnackbar";
import { updateAfterUnlike } from "@/features/post/components/action/postActionHelper";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { useRemoveLikeMutation } from "@/generated/generated-hooks";

type ImageCardRemoveLikeButton = {
  postId: string;
  logEvent?: AnalyticsEventEnum;
};

export const ImageCardRemoveLikeButton: FC<
  ImageCardRemoveLikeButton &
    React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >
> = (props) => {
  const { postId, children, logEvent } = props;
  const { openSnackbar } = useSnackbar();
  const { t } = useTranslation("common");
  const { user } = useUserContext();
  const { openDialog } = useUIContext();
  const [removeLike, { error, loading }] = useRemoveLikeMutation({
    variables: { input: postId },
    update: updateAfterUnlike({ postId, variables: { input: postId } }),
  });

  const onClickLike = async () => {
    if (loading) return;
    if (!user?._id) return openDialog(DialogEnum.Login);
    try {
      firebase
        .analytics()
        .logEvent(logEvent ?? AnalyticsEventEnum.ImageCardClickRemoveLike, {
          postId,
        });
      await removeLike();
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          mutation: "removeLike",
        },
      });
      openSnackbar({
        message: t("common:errors.fail"),
        severity: "error",
      });
    }
  };

  return (
    <>
      <div className="flex touchable" onClick={onClickLike}>
        {children ? (
          children
        ) : (
          <HeartIconFill
            className="text-primary w-4 h-4"
            alt="remove like icon"
          />
        )}
      </div>
    </>
  );
};
